import React, { useEffect, useState } from 'react';

/**
 * 编辑器实时预览组件
 * 只读 GET 后台接口（egg session 登录态，见 h5/src/utils/fetch.js 自动注入 CSRF），
 * 不写数据、不碰小程序的 sessionCode。区块结构对齐 mini/（Taro）同名组件。
 */

// 图片：src 数组取首项 + 域名前缀（对齐 mini utils/common.ts getImageUrl）
export function imgUrl(src) {
    if (!src) return '';
    let url = src;
    if (Array.isArray(src)) {
        url = src[0];
    }
    if (!url) return '';
    return url.indexOf('://') >= 0 ? url : `https://img.yxdj.tech/${url}`;
}

// 价格：分 → 元（对齐 mini utils/common.ts priceShow）
export function priceShow(price, fixed = 2) {
    if (price === undefined || price === null || price === '') {
        return '--';
    }
    return (price / 100).toFixed(fixed);
}

// 只读 GET 拉取数据，成功取 data.rows（数组），失败返回空数组
async function fetchData(path) {
    try {
        const res = await fetch(`/api${path}`);
        const result = await res.json();
        if (result?.success) {
            const rows = result.data?.rows;
            return Array.isArray(rows) ? rows : [];
        }
        return [];
    } catch (e) {
        return [];
    }
}

// 商品卡片：
// column '1' → 单列横向卡（对齐 mini goods-item：左图 110px + 右信息 + 购买按钮）
// 否则     → 双列竖向卡（对齐 mini the-goods：图全宽 + 标题 + 价格/已售）
function GoodsCard({ data, column }) {
    const { title, shortDescription, description, mainImages, price, marketPrice, type } = data || {};
    const src = imgUrl(mainImages);
    const isColumn1 = column === '1';
    const sold = (data?.realSaleNum || 0) + (data?.offlineSaleNum || 0);
    return (
        <div className={`render-goods-item ${isColumn1 ? '' : 'grid'}`}>
            {src ? (
                <img src={src} alt={title || ''} className="render-goods-image" />
            ) : (
                <div className="render-goods-image placeholder">图</div>
            )}
            <div className="render-goods-info">
                <div className="render-goods-info-content">
                    <div className="render-goods-title">{title || '未命名商品'}</div>
                    {isColumn1 && shortDescription ? (
                        <div className="render-goods-desc">{shortDescription || description}</div>
                    ) : null}
                </div>
                <div className="render-goods-price-row">
                    {isColumn1 ? (
                        <>
                            <span className="render-goods-price-wrapper">
                                <span className="render-goods-price">¥ {priceShow(price)}</span>
                                {marketPrice ? (
                                    <span className="render-goods-origin">¥ {priceShow(marketPrice, 0)}</span>
                                ) : null}
                            </span>
                            <span className="render-goods-buy">立即{type === 'NORMAL' ? '预约' : '购买'}</span>
                        </>
                    ) : (
                        <>
                            <span className="render-goods-price-wrapper">
                                <span className="render-goods-price">¥{priceShow(price)}</span>
                                {marketPrice ? (
                                    <span className="render-goods-origin"> ¥{priceShow(marketPrice, 0)}</span>
                                ) : null}
                            </span>
                            <span className="render-goods-sold">已售 {sold}</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// 优惠券卡（对齐 mini coupon-tmp）
function CouponCard({ data }) {
    const { title, type, triggerAmount, offerAmount, afterDays } = data || {};
    return (
        <div className="render-coupon-item">
            <div className="render-coupon-left">
                {type === 1 ? (
                    <div className="render-coupon-money">¥{priceShow(offerAmount)}</div>
                ) : null}
                {type === 2 ? (
                    <div className="render-coupon-discount">{offerAmount / 10}<span>折</span></div>
                ) : null}
                <div className="render-coupon-condition">
                    {triggerAmount > 0 ? `满${priceShow(triggerAmount)}可用` : '无门槛'}
                </div>
            </div>
            <div className="render-coupon-right">
                <div className="render-coupon-title">{title || '优惠券'}</div>
                <div className="render-coupon-desc">领取后{afterDays}天内有效</div>
            </div>
            <div className="render-coupon-btn">领<br />取</div>
        </div>
    );
}

/**
 * 商品列表（对齐 mini goods-list）：goodsIds 非空按选中 id 过滤并保持顺序，
 * 否则取前 5；column '1' 单列，其余双列。
 */
export function GoodsListPreview({ accId, shopId, goodsIds, column, title }) {
    const [list, setList] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!shopId) return;
        let unmounted = false;
        Promise.all([
            fetchData(`/goods/my-list?accId=${accId}&shopId=${shopId}&page=1&pageSize=10000`),
            fetchData(`/goods/proxy-list?accId=${accId}&shopId=${shopId}&page=1&pageSize=10000`),
        ]).then(([myRows, proxyRows]) => {
            if (unmounted) return;
            // 合并自有 + 代理商品（对齐 comp-form/choose-goods.js）
            const merged = [...(myRows || []), ...(proxyRows || [])];
            setList(merged);
            setLoaded(true);
        });
        return () => { unmounted = true; };
    }, [accId, shopId]);

    const ids = goodsIds || [];
    let showList = list;
    if (ids.length > 0) {
        const idMap = {};
        list.forEach(g => { idMap[g.goodsId] = g; });
        // 保持用户选择顺序，未命中的 id 忽略
        showList = ids.map(id => idMap[id]).filter(Boolean);
    } else {
        showList = list.slice(0, 5);
    }

    const col = String(column || '');
    const isColumn1 = col === '1';

    if (!loaded) return <div className="render-comp-empty">加载中…</div>;
    if (showList.length === 0) {
        return <div className="render-comp-empty">{ids.length > 0 ? '所选商品不存在或已下架' : '请选择商品'}</div>;
    }

    return (
        <div className="render-goods-list-content">
            {title ? <div className="render-goods-list-title">{title}</div> : null}
            <div className={`render-goods-list ${isColumn1 ? '' : 'render-goods-grid'}`}>
                {showList.map(item => (
                    <div className="render-goods-item-wrap" key={item.goodsId}>
                        <GoodsCard data={item} column={col} />
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * 热门商品（对齐 mini hot-list）：同商品接口，客户端按销量 realSaleNum+offlineSaleNum 降序取前 5。
 */
export function HotListPreview({ accId, shopId }) {
    const [list, setList] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!shopId) return;
        let unmounted = false;
        Promise.all([
            fetchData(`/goods/my-list?accId=${accId}&shopId=${shopId}&page=1&pageSize=10000`),
            fetchData(`/goods/proxy-list?accId=${accId}&shopId=${shopId}&page=1&pageSize=10000`),
        ]).then(([myRows, proxyRows]) => {
            if (unmounted) return;
            const merged = [...(myRows || []), ...(proxyRows || [])];
            const sorted = merged.slice().sort((a, b) => {
                return (b.realSaleNum || 0) + (b.offlineSaleNum || 0) - (a.realSaleNum || 0) - (a.offlineSaleNum || 0);
            });
            setList(sorted.slice(0, 5));
            setLoaded(true);
        });
        return () => { unmounted = true; };
    }, [accId, shopId]);

    if (!loaded) return <div className="render-comp-empty">加载中…</div>;
    if (list.length === 0) return <div className="render-comp-empty">暂无热销商品</div>;

    return (
        <div className="render-hot-list-content">
            <div className="render-hot-list-title">热销列表</div>
            <div className="render-hot-list">
                {list.map(item => (
                    <div className="render-goods-item-wrap" key={item.goodsId}>
                        <GoodsCard data={item} column="1" />
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * 优惠券展现（对齐 mini benefit/coupon-tmp）：按选中 couponList id 过滤 + status===1 取有效券。
 */
export function BenefitPreview({ shopId, couponList }) {
    const [list, setList] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!shopId) return;
        let unmounted = false;
        fetchData(`/coupon/list?shopId=${shopId}`).then(rows => {
            if (unmounted) return;
            setList(rows || []);
            setLoaded(true);
        });
        return () => { unmounted = true; };
    }, [shopId]);

    const ids = couponList || [];
    let showList = list.filter(i => i.status === 1);
    if (ids.length > 0) {
        const idSet = {};
        ids.forEach(id => { idSet[id] = true; });
        showList = showList.filter(i => idSet[i.couponTmpId]);
    }

    if (!loaded) return <div className="render-comp-empty">加载中…</div>;
    if (showList.length === 0) return <div className="render-comp-empty">请选择优惠券</div>;

    return (
        <div className="render-coupon-content">
            {showList.map(item => (
                <CouponCard key={item.couponTmpId} data={item} />
            ))}
        </div>
    );
}

/**
 * 已售消息轮播（对齐 mini rolling-barrage）：展示「用户 X 购买了 商品/次卡：Y」。
 */
export function RollingBarragePreview({ shopId }) {
    const [list, setList] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!shopId) return;
        let unmounted = false;
        fetchData(`/userApp/shop/order_show_list?shopId=${shopId}`).then(rows => {
            if (unmounted) return;
            setList(rows || []);
            setLoaded(true);
        });
        return () => { unmounted = true; };
    }, [shopId]);

    if (!loaded) return <div className="render-comp-empty">加载中…</div>;
    if (list.length === 0) return <div className="render-comp-empty">暂无已售消息</div>;

    // 复制一遍实现无缝循环滚动
    const showList = [...list, ...list];

    return (
        <div className="render-rolling-content">
            <div className="render-rolling-track">
                {showList.map((item, index) => {
                    const { name, goodsType, goodsInfo = {}, goodsCardTmpInfo = {} } = item;
                    let type = '商品';
                    let showName = goodsInfo.title;
                    if (goodsType === 'TIMES_CARD') {
                        type = '次卡';
                        showName = goodsCardTmpInfo.title;
                    }
                    return (
                        <div className="render-rolling-item" key={index}>
                            用户 <span className="render-rolling-name">{name}</span> 购买了{type}: <span className="render-rolling-goods">{showName}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * 顶部搜索栏（对齐 mini search-bar）：静态 UI。
 */
export function SearchBarPreview({ canChooseShop }) {
    return (
        <div className="render-search-bar">
            {canChooseShop ? (
                <span className="render-search-shop">店铺</span>
            ) : null}
            <span className="render-search-btn">搜索更多生活服务</span>
        </div>
    );
}

/**
 * 开屏弹框（对齐 mini operation-dialog）：取首个 showList 项，
 * type==='coupon' → 拉优惠券按 couponIds 过滤渲染券卡；
 * 其余（goodsCard/goods/other）→ 配置的 image 真实渲染。
 */
export function OperationDialogPreview({ shopId, showList }) {
    const [couponRows, setCouponRows] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const list = showList || [];
    const first = list[0];
    const isCoupon = first?.type === 'coupon';

    useEffect(() => {
        if (!shopId || !isCoupon) {
            setLoaded(true);
            return;
        }
        let unmounted = false;
        fetchData(`/coupon/list?shopId=${shopId}`).then(rows => {
            if (unmounted) return;
            setCouponRows(rows || []);
            setLoaded(true);
        });
        return () => { unmounted = true; };
    }, [shopId, isCoupon]);

    if (list.length === 0) {
        return <div className="render-comp-empty">请配置弹框内容</div>;
    }

    if (isCoupon) {
        const ids = first.couponIds || [];
        let showList2 = couponRows.filter(i => i.status === 1);
        if (ids.length > 0) {
            const idSet = {};
            ids.forEach(id => { idSet[id] = true; });
            showList2 = showList2.filter(i => idSet[i.couponTmpId]);
        }
        if (!loaded) return <div className="render-comp-empty">加载中…</div>;
        if (showList2.length === 0) return <div className="render-comp-empty">该优惠券当前不可领取</div>;
        return (
            <div className="render-operation-dialog">
                <div className="render-coupon-content">
                    {showList2.slice(0, 3).map(item => (
                        <CouponCard key={item.couponTmpId} data={item} />
                    ))}
                </div>
                <div className="render-coupon-get-all">立即领取</div>
            </div>
        );
    }

    // goodsCard / goods / other：配置的 image 真实渲染
    const src = imgUrl(first.image);
    return (
        <div className="render-operation-dialog">
            {src ? (
                <img src={src} alt="弹框内容" className="render-operation-dialog-image" />
            ) : (
                <div className="render-operation-dialog-placeholder">弹框图片</div>
            )}
        </div>
    );
}
